import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from 'store/hooks';
import { Nullable } from '@linetweet/linetweet-ui';
import { LocaleKeyEnum } from '../../types';

const LOCAL_STORAGE_LOCALE_KEY = 'language';
const WINDOW_SEARCH_PARAMS_LOCALE_KEY = 'language';

const getLocaleFromStorage = (): Nullable<LocaleKeyEnum> => (
  localStorage.getItem(LOCAL_STORAGE_LOCALE_KEY) as LocaleKeyEnum
);

const setLocaleToStorage = (locale: LocaleKeyEnum): void => {
  localStorage.setItem(LOCAL_STORAGE_LOCALE_KEY, locale);
};

const removeLocaleFromStorage = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_LOCALE_KEY);
};

const possibleLocales = Object.values(LocaleKeyEnum);

export function useLocaleSelection() {
  const browserLocale = window.navigator.language as LocaleKeyEnum;
  const defaultLocale = LocaleKeyEnum.deDE;

  const storeState = useAppSelector((state) => state.store);

  const storeLocale = useMemo(() => {
    if (storeState.data?.defaultLanguage) {
      return storeState.data?.defaultLanguage;
    }
    return defaultLocale;
  }, [storeState, defaultLocale]);

  const [locale, setLocale] = useState<LocaleKeyEnum>(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const localeParam = queryParams.get(WINDOW_SEARCH_PARAMS_LOCALE_KEY) as LocaleKeyEnum | null;

    if (localeParam && possibleLocales.includes(localeParam)) {
      setLocaleToStorage(localeParam);

      return localeParam;
    }

    const storageLocale = getLocaleFromStorage();
    if (storageLocale && possibleLocales.includes(storageLocale)) {
      return storageLocale;
    }

    if (browserLocale && possibleLocales.includes(browserLocale)) {
      return browserLocale;
    }

    return storeLocale;
  });

  useEffect(() => {
    const storageLocale = getLocaleFromStorage();
    if (storageLocale && possibleLocales.includes(storageLocale)) {
      return;
    }
    if (browserLocale && possibleLocales.includes(browserLocale)) {
      return;
    }

    setLocale(storeLocale);
  }, [storeLocale, browserLocale]);

  const changeLocale = useCallback((updatedLocale: LocaleKeyEnum) => {
    setLocaleToStorage(updatedLocale);
    setLocale(updatedLocale);
  }, []);

  const resetLocale = useCallback(() => {
    removeLocaleFromStorage();
    if (browserLocale && possibleLocales.includes(browserLocale)) {
      setLocale(browserLocale);
      return;
    }
    setLocale(defaultLocale);
  }, [browserLocale, defaultLocale]);

  return { locale, setLocale: changeLocale, resetLocale };
}
